import { Avatar, Card, KpiTile, RowMeter, formatNumber, formatPercent } from "@omnabase/ui";
import { change, formatMinutes, type TeamRead } from "@omnabase/shared";
import { useFetch } from "../../lib/inbox";
import { useSession } from "../../shell/store";
import { NothingYet, ReportLoading, versus, type ReportContext } from "./ReportsScreen";

/** Team (brief In scope 1): resolved by people, the first reply by a person, "Open now" live, and who resolved what with the AI as a
 *  row of its own; only the people with current access to the project in scope are listed. Times are wall clock, and the note says so.
 *  The per-person table is for holders of `team` (the answer's `scope` "team"); everyone else ("you") reads the AI's row and their own,
 *  labelled "You", and a sentence saying where the rest went (DECISIONS "2026-10-05 — team member stats for administrators only"). */
const both = (min: number | null) => (min === null ? "—" : Object.values(formatMinutes(min)).join(" "));

export function ReportTeam({ ctx }: { ctx: ReportContext }) {
  const { workspace } = useSession();
  const r = useFetch<TeamRead>(`/api/v1/w/${ctx.slug}/reports/team?${ctx.query}`, [ctx.query]);
  if (!r.data) return r.error ? <p className="ob-report__note">{r.error}</p> : <ReportLoading />;
  const d = r.data; const c = d.current, p = d.previous;
  if (d.received === 0 && c.resolvedByPeople === 0 && c.resolvedByAi === 0 && d.openNow.open === 0) return <NothingYet ctx={ctx} />;
  const reply = c.humanReplyMin === null ? { value: "—", unit: "" } : formatMinutes(c.humanReplyMin);
  const resolved = c.resolvedByPeople + c.resolvedByAi;
  const top = Math.max(1, ...d.rows.map((x) => x.resolved));
  const member = (id: string) => workspace.members.find((m) => m.userId === id);
  const own = d.scope === "you";
  return (
    <>
      <div className="ob-kpis">
        <KpiTile value={formatNumber(c.resolvedByPeople)} unit="tickets" label="Resolved by people" sub={versus(change(c.resolvedByPeople, p.resolvedByPeople), ctx.range.days)} />
        <KpiTile value={reply.value} unit={reply.unit} label="First reply by a person · median" sub={versus(change(c.humanReplyMin ?? 0, p.humanReplyMin ?? 0), ctx.range.days)} />
        <KpiTile value={formatNumber(d.openNow.open)} unit="now" label="Open now" sub={`${formatNumber(d.openNow.unassigned)} of them unassigned`} />
        <KpiTile value={resolved ? formatPercent(c.resolvedByPeople / resolved) : "—"} label="Share resolved by people" sub={`The AI alone: ${formatNumber(c.resolvedByAi)}`} />
      </div>
      <Card title="Who resolved what" className="ob-card--table">
        <div className="ob-dtable-card">
          <table className="ob-dtable">
            <thead><tr><th>Name</th><th className="ob-dtable__num">Resolved</th><th className="ob-dtable__col--phone">Share</th><th className="ob-dtable__num">First reply</th><th className="ob-dtable__num ob-dtable__col--phone">Time to resolve</th></tr></thead>
            <tbody>
              {d.rows.map((row) => {
                const m = row.kind === "person" ? member(row.id) : undefined;
                const name = own && row.kind === "person" ? "You" : row.name; // with "you" the one person row is the caller's
                return (
                  <tr key={row.id}>
                    <td className="ob-dtable__grow"><span className="ob-dtable__person">{row.kind === "ai" ? <Avatar size="sm" initials="AI" ai icon="sparkles" /> : <Avatar size="sm" initials={m?.initials ?? row.name.slice(0, 2).toUpperCase()} src={m?.imageUrl ?? undefined} />}<span className="ob-dtable__name" title={name}>{name}</span></span></td>
                    <td className="ob-dtable__num">{formatNumber(row.resolved)}</td>
                    <td className="ob-dtable__col--phone"><span className="ob-metercell"><RowMeter value={row.resolved / top} tone={row.kind === "ai" ? "ai" : undefined} /><span className="ob-metercell__value">{resolved ? formatPercent(row.resolved / resolved) : "—"}</span></span></td>
                    <td className="ob-dtable__num">{both(row.firstReplyMin)}</td>
                    <td className="ob-dtable__num ob-dtable__col--phone">{both(row.resolveMin)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      {own && <p className="ob-report__note">Your administrators see the whole team.</p>}
      <p className="ob-report__note">Times are wall clock. A reply on a Saturday counts its minutes like any other. {own ? "The numbers above count the whole team's work." : "The table lists the people who can reply on the project in scope today; what a former member resolved stays in the count above."}</p>
    </>
  );
}
